import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAbility } from '@casl/react';
import { createGlobalStyle } from 'styled-components';
import { useTranslate } from '@bynder/localization';

import { IconCampaign, IconCaretDown } from '@bynder/icons';
import { Flex } from '@bynder/design-system';
import { isView, isMainView } from '../../../helpers/windowLocation';
import { useOutsideClick } from '../../../helpers/useOutsideClick';
import { initModalLinks, getWorkflowData, initCTAMediaUploadButton } from '../../../legacy/legacy';
import { AbilityContext } from '../../../abilities/Can';
import { openWorkflowModalOnLoad } from './openPresetModalOnload';
import { iconSizeLarge } from '../../../helpers/iconSizes';

const workflow: WindowWorkflow = getWorkflowData();
openWorkflowModalOnLoad(workflow.visiblePresets);

interface DropdownItem {
	id?: string;
	label: string;
	url?: string;
	href?: string;
	className?: string;
	dataTargetSelector?: string;
	dataImportURL?: string;
	dataAction?: string;
	dataInit?: string;
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

/*
 * bynder-platform/bynder/views/navigation/header.cfm: 379
 */

const verticalPositionDropdown = 120;
const marginBottomDropdown = 30;

const maxHeightDropdown = `${
	window.innerHeight - verticalPositionDropdown - marginBottomDropdown
}px`;
export function CTAWorkflow() {
	const refDropdown = useRef<HTMLUListElement>(null);
	const ability = useAbility(AbilityContext);
	const { translate } = useTranslate();

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [jobViewItems, setJobViewItems] = useState<DropdownItem[]>([]);
	const [workflowItems, setWorkflowItems] = useState<DropdownItem[]>([]);

	useEffect(() => {
		if (workflowItems.length || jobViewItems.length) {
			initModalLinks();
			initCTAMediaUploadButton();
		}
	}, [workflowItems, jobViewItems]);

	useOutsideClick(refDropdown, () => {
		setIsDropdownOpen(false);
	});

	const buildJobViewItems = useCallback(() => {
		if (!isView('job/view')) {
			return [];
		}

		const items: DropdownItem[] = [];

		if (ability.can('select', 'workflow_add_asset')) {
			items.push({
				label: translate('workflow.AddAsset'),
				href: '#',
				className: 'focus-button',
				dataTargetSelector: '.add-asset.templates a',
			});
		}

		if (
			ability.cannot('select', 'workflow_add_asset') &&
			ability.can('select', 'workflow_add_asset_template')
		) {
			items.push({
				id: 'cta-wf-media-upload',
				label: translate('workflow.AddAsset'),
				href: '#',
				className: '',
				dataImportURL: workflow.uploadURL,
			});
		}

		if (ability.can('select', 'workflow_add_task')) {
			items.push({
				label: translate('workflow.TaskAdd'),
				href: '#',
				className: 'focus-button',
				dataTargetSelector: '#new-task input.task-name-input',
				dataAction: 'focus',
				onClick: (e: React.MouseEvent) => {
					e.preventDefault();

					const taskInput = document.querySelector<HTMLInputElement>(
						'.wf-task-edit-value input#tsk-name-new-task',
					);
					taskInput?.click();
					taskInput?.focus();
				},
			});
		}

		if (ability.can('select', 'workflow_add_stage')) {
			items.push({
				label: translate('workflow.StageAdd'),
				href: `/workflow/stage/add/${workflow.jobId}/`,
				className: 'modal-link',
				dataInit: 'Job.edit',
			});
		}

		return items;
	}, [ability, translate]);

	const buildWorkflowItems = useCallback((): DropdownItem[] => {
		const jobItems = [];

		// New campaign
		if (ability.can('select', 'workflow_add_campaign')) {
			jobItems.push({
				url: '/workflow/campaign/add/',
				label: translate('workflow.Campaign'),
				className: 'modal-link',
				dataInit: 'Campaign.new',
			});
		}

		// Add job
		if (ability.can('select', 'workflow_add_job')) {
			const idURL = workflow.campaignId ? workflow.campaignId + '/' : '';

			jobItems.push({
				label: translate('workflow.Job'),
				url: '/workflow/job/add/' + idURL,
				className: 'job-add modal-link',
				dataInit: 'Job.new',
			});
		}

		// Add preset
		if (ability.can('select', 'workflow_add_preset')) {
			jobItems.push({
				label: translate('workflow.AddPreset'),
				url: '/workflow/preset/add/',
			});
		}

		// Use preset
		if (ability.can('select', 'workflow_use_preset')) {
			let presetURL = '';

			workflow.visiblePresets.forEach((preset) => {
				switch (preset.type) {
					case 'campaign':
						presetURL = `/workflow/campaign/add/?preset=${preset.id}`;
						break;

					case 'job':
						if (workflow.campaignId) {
							presetURL = `/workflow/job/add/${workflow.campaignId}/?preset=${preset.id}`;
						} else {
							presetURL = `/workflow/job/add/?preset=${preset.id}`;
						}

						break;
				}

				const capitalizedType = `${preset.type[0].toUpperCase() || ''}${
					preset.type.slice(1) || ''
				}`;

				jobItems.push({
					id: preset.id,
					label: preset.name,
					url: presetURL,
					className: 'job-add-preset modal-link',
					dataInit: `${capitalizedType}.new`,
				});
			});
		}

		return jobItems;
	}, [ability, translate]);

	useEffect(() => {
		const jobItems = buildJobViewItems();
		setJobViewItems(jobItems);

		const wfItems = buildWorkflowItems();
		setWorkflowItems(wfItems);
	}, [ability.rules, buildJobViewItems, buildWorkflowItems]);

	const toggleDropdown = () => {
		setIsDropdownOpen((prevState) => !prevState);
	};

	// TODO: move this to the workflow data object from Coldfusion
	const showCreateNew =
		ability.can('show', 'workflow_create_new') &&
		(isMainView() ||
			isView('main/index') ||
			isView('campaign/overview') ||
			isView('campaign/dashboard') ||
			isView('campaign/calendar') ||
			isView('job/overview'));

	const firstJVItem: DropdownItem = jobViewItems[0];

	return (
		<>
			<GlobalStyle />
			<ul className="cta-main-container" id="uploadContainer" ref={refDropdown}>
				{jobViewItems.length > 1 && (
					<>
						<li>
							<a
								href="#"
								id={firstJVItem.id}
								className={`cta-main cta-media upload focus-button ${
									jobViewItems.length === 1 ? 'no-dropdown' : ''
								}`}
								onClick={(item) => {
									const target = $(item.target).data('target-selector');
									$(target).trigger('click');
								}}
								data-target-selector={firstJVItem.dataTargetSelector}
								data-importurl={firstJVItem.dataImportURL}
								data-action={firstJVItem.dataAction}
								data-init={firstJVItem.dataInit}
							>
								<Flex gap="2" alignItems="center">
									<IconCampaign size={iconSizeLarge} />
									{firstJVItem.label}
								</Flex>
							</a>
						</li>

						{/* CONTEXTUAL MENU */}
						<li className={isDropdownOpen ? 'show' : ''}>
							<a
								href="#"
								onClick={toggleDropdown}
								className="cta-main cta-dropdown-trigger"
							>
								<IconCaretDown size={iconSizeLarge} />
							</a>
							<ul className="cta-dropdown max-height-unified-header">
								{jobViewItems.slice(1).map((item: DropdownItem) => (
									<li key={item.label}>
										<a
											href={item.href}
											className={item.className}
											data-target-selector={item.dataTargetSelector}
											data-importurl={item.dataImportURL}
											data-action={item.dataAction}
											data-init={item.dataInit}
											onClick={item.onClick}
										>
											{item.label}
										</a>
									</li>
								))}
							</ul>
						</li>
					</>
				)}

				{showCreateNew && workflowItems.length > 0 && (
					<>
						<li role="button" onClick={toggleDropdown} onKeyPress={toggleDropdown}>
							<a href="#" className="cta-main cta-open-only">
								<Flex gap="2" alignItems="center">
									<IconCampaign size={iconSizeLarge} />
									{translate('workflow.CreateNew')}...
								</Flex>
							</a>
						</li>

						{/* CONTEXTUAL MENU */}
						<li className={isDropdownOpen ? 'show' : ''}>
							<a
								href="#"
								onClick={toggleDropdown}
								className="cta-main cta-dropdown-trigger"
							>
								<IconCaretDown size={iconSizeLarge} />
							</a>

							<ul className="cta-dropdown max-height-unified-header" key="listItem">
								{workflowItems.map((item: DropdownItem) => (
									<li key={item.id}>
										<a
											id={item.id}
											href={item.url}
											className={item.className}
											data-init={item.dataInit}
										>
											{item.label}
										</a>
									</li>
								))}
							</ul>
						</li>
					</>
				)}
			</ul>
		</>
	);
}

const GlobalStyle = createGlobalStyle`
	.max-height-unified-header {
		max-height: ${maxHeightDropdown};
	}
`;
